import type { OtcData } from '@/composables/useOtc';
import { createHHAxios } from '@/references/axios/axios';
import { HHAPIService } from '@/references/axios/base';
import type { ToEurArgs, ToEurReturn } from '@/references/axios/otc/types';
import type { Fees, Quote } from '@/references/axios/transactions/types';
import type { RequestOptions } from '@/references/axios/types';
import type { Address, ClientType } from '@/references/base';
import { getAvailableNetworks, Network } from '@/references/network';
import { substituteAssetAddressIfNeeded, type Token } from '@/references/tokens';

export class HHAPIOtcService extends HHAPIService {
  constructor(
    private baseURL: string,
    private clientType: ClientType
  ) {
    super('otc.service');
  }

  public getAvailableNetworks(): Network[] {
    return getAvailableNetworks();
  }

  public async toEur<In extends Token>(
    params: ToEurArgs<In>,
    opts?: RequestOptions
  ): Promise<ToEurReturn<In>> {
    const instance = createHHAxios({
      baseURL: this.baseURL,
      headers: this.getClientTypeHeaders(this.clientType)
    });
    const resp = await instance.request<
      {
        tokenAmount: string;
        rate: string;
        EURAmount: string;
      },
      {
        tokenAmount: string;
        fromToken: {
          address: Address;
          decimals: number;
          network: Network;
        };
      }
    >({
      url: '/v1/private/convert/fromTokenToEUR',
      method: 'POST',
      data: {
        tokenAmount: params.amount,
        fromToken: {
          address: params.inputToken.address,
          decimals: params.inputToken.decimals,
          network: params.inputToken.network
        }
      },
      withCredentials: true,
      opts
    });

    return {
      inputAmount: resp.data.payload.tokenAmount,
      inputToken: params.inputToken,
      outputAmount: resp.data.payload.EURAmount,
      rate: resp.data.payload.rate
    };
  }

  async newQuote(
    otcData: OtcData,
    chainId: number,
    recipientId: number,
    fees: Fees,
    opts?: RequestOptions
  ): Promise<Quote> {
    const instance = createHHAxios({
      baseURL: this.baseURL,
      headers: this.getClientTypeHeaders(this.clientType)
    });
    const resp = await instance.request<
      { quote: Quote },
      {
        beneficiaryId: number;
        tokenAddress: string;
        chainId: number;
        tokenAmount: string;
        eurAmount: string;
        transactionFee: string;
        networkFee: string;
        transferFee: string;
      }
    >({
      method: 'POST',
      data: {
        beneficiaryId: recipientId,
        tokenAddress: substituteAssetAddressIfNeeded(
          otcData.fromToken.address,
          otcData.fromToken.network
        ),
        chainId: chainId,
        tokenAmount: otcData.fromAmount,
        eurAmount: otcData.toAmount,
        transactionFee: fees.transactionFee,
        networkFee: fees.networkFee,
        transferFee: fees.transferFee
      },
      url: `/v1/private/quotes`,
      withCredentials: true,
      opts
    });

    return resp.data.payload.quote;
  }

  async getQuoteById(quoteId: string, opts?: RequestOptions): Promise<Quote> {
    const instance = createHHAxios({
      baseURL: this.baseURL,
      headers: this.getClientTypeHeaders(this.clientType)
    });
    const resp = await instance.request<{ quote: Quote }>({
      method: 'GET',
      url: `/v1/private/quotes/${quoteId}`,
      withCredentials: true,
      opts
    });

    return resp.data.payload.quote;
  }

  async getFees(beneficiaryId: number, opts?: RequestOptions): Promise<Fees> {
    const instance = createHHAxios({
      baseURL: this.baseURL,
      headers: this.getClientTypeHeaders(this.clientType)
    });
    const resp = await instance.request<{ additionalFeeEUR: string }, { beneficiaryId: number }>({
      method: 'POST',
      url: '/v1/private/fee/calc',
      data: {
        beneficiaryId
      },
      withCredentials: true,
      opts
    });

    return {
      transactionFee: resp.data.payload.additionalFeeEUR,
      networkFee: '0',
      transferFee: '0'
    };
  }
}
